/**
 * Phase 20.8 — My Library / Licensed Beats
 *
 * Lists every paid beat the current user has licensed. Each card shows
 * cover, title, producer name, tier, price, purchased-at, and three
 * actions:
 *   • ⬇ Download — direct link to /api/beats/{id}/download (server-side
 *     gated on the matching beat_sales row, so even copying the URL
 *     before purchase is rejected)
 *   • 📄 License Terms — modal-free, shows tier terms inline on demand
 *   • ↗ Open Beat — deep link to /beats/{id} for full detail / share
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import SharedNavigation from "@/components/SharedNavigation";
import { useCurrentUser } from "@/lib/useCurrentUser";
import { optimizeImage } from "@/lib/mediaUrls";

const formatPrice = (usd, currency = "usd") => {
  if (!usd && usd !== 0) return "—";
  const n = Number(usd);
  return `${currency === "usd" ? "$" : ""}${n.toFixed(2)}${currency === "usd" ? "" : ` ${currency.toUpperCase()}`}`;
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return iso; }
};

const LicenseBadge = ({ label }) => (
  <span
    className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-200 border border-emerald-500/40"
    data-testid="library-license-badge"
  >
    {label || "License"}
  </span>
);

const LicensedBeatCard = ({ purchase }) => {
  const [showTerms, setShowTerms] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
  const downloadHref = `${BACKEND_URL}${purchase.download_url}`;

  const handleDownload = () => {
    // The endpoint streams the audio file with the same auth cookie/header
    // logic axios uses. We open it in a new tab so the browser's native
    // download/playback chrome kicks in. If the user isn't authed, the
    // backend returns 402/401 and the browser shows the error.
    window.open(downloadHref, "_blank", "noopener,noreferrer");
  };

  return (
    <article
      className="rounded-2xl border border-purple-500/20 bg-gradient-to-br from-[#11111a] to-[#0a0a12] overflow-hidden shadow-lg hover:border-purple-400/40 transition-colors"
      data-testid="library-beat-card"
    >
      <div className="flex gap-4 p-4">
        <Link
          to={purchase.detail_url}
          className="shrink-0 w-24 h-24 rounded-xl overflow-hidden ring-1 ring-white/10"
          data-testid="library-beat-cover-link"
        >
          <img
            src={optimizeImage(purchase.beat?.cover_url || "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><rect width='100' height='100' fill='%23222'/></svg>", 'thumb')}
            alt={purchase.beat?.title || "Beat"}
            className="w-full h-full object-cover"
          />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <Link
                to={purchase.detail_url}
                className="block font-black text-white text-lg leading-tight truncate hover:text-purple-200 transition-colors"
                data-testid="library-beat-title"
                title={purchase.beat?.title}
              >
                {purchase.beat?.title || "Untitled beat"}
              </Link>
              <Link
                to={`/producer/${purchase.producer?.user_id || ""}`}
                className="block text-xs text-purple-200/70 mt-0.5 truncate hover:text-purple-100 transition-colors"
                data-testid="library-beat-producer"
              >
                by {purchase.producer?.name || "Producer"}
              </Link>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <LicenseBadge label={purchase.tier_label} />
                <span className="text-[11px] text-gray-400 tabular-nums" data-testid="library-beat-price">
                  {formatPrice(purchase.amount_usd, purchase.currency)}
                </span>
                <span className="text-[11px] text-gray-500" data-testid="library-beat-date">
                  · Purchased {formatDate(purchase.purchased_at)}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-3">
            <button
              onClick={handleDownload}
              data-testid="library-beat-download-btn"
              className="h-9 px-3 rounded-full text-xs font-bold bg-gradient-to-r from-emerald-500 to-emerald-400 hover:from-emerald-400 hover:to-emerald-300 text-emerald-950 transition-colors"
              title="Download licensed beat"
            >
              ⬇ Download
            </button>
            <button
              onClick={() => setShowTerms((v) => !v)}
              data-testid="library-beat-terms-btn"
              className="h-9 px-3 rounded-full text-xs font-bold bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors"
            >
              📄 {showTerms ? "Hide" : "License Terms"}
            </button>
            <Link
              to={purchase.detail_url}
              data-testid="library-beat-open-link"
              className="h-9 px-3 rounded-full text-xs font-bold bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors flex items-center"
            >
              ↗ Open Beat
            </Link>
          </div>

          {showTerms && (
            <div
              className="mt-3 p-3 rounded-lg bg-black/40 border border-white/10 text-xs text-gray-200 whitespace-pre-wrap"
              data-testid="library-beat-terms-body"
            >
              {purchase.tier_terms || "No terms recorded for this tier — open the beat detail page for the producer's license language."}
              <div className="text-[10px] text-gray-500 mt-2 font-mono break-all">
                Receipt: {purchase.session_id}
              </div>
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

const LicensedBeatsPage = () => {
  const { currentUser } = useCurrentUser();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/beats/me/purchases");
        if (cancelled) return;
        setPurchases(r.data?.purchases || []);
      } catch (err) {
        if (err?.response?.status === 401) toast.error("Sign in to view your library");
        else toast.error("Could not load your library");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-[#08080c]" data-testid="licensed-beats-page">
      <SharedNavigation currentUser={currentUser} />
      <div className="pt-24 pb-24 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <header className="mb-8">
          <p className="text-[11px] uppercase tracking-widest text-emerald-300 font-bold">My Library</p>
          <h1 className="text-3xl sm:text-4xl font-black text-white">
            📚 My Licensed Beats
          </h1>
          <p className="text-sm text-gray-400 mt-1 max-w-2xl">
            Every beat you've purchased on RGM Studio — download anytime,
            check your license terms, and revisit the original beat page.
          </p>
        </header>

        {loading ? (
          <div className="text-center text-gray-400 py-16" data-testid="library-loading">
            Loading your library…
          </div>
        ) : purchases.length === 0 ? (
          <div
            className="text-center py-16 px-4 rounded-2xl border border-white/10 bg-white/5"
            data-testid="library-empty"
          >
            <div className="text-5xl mb-3">🛒</div>
            <h2 className="text-xl font-bold text-white">No licenses yet</h2>
            <p className="text-sm text-gray-400 mt-1 max-w-md mx-auto">
              When you license a beat from a producer, it'll show up here with
              a download button and your receipt.
            </p>
            <Link
              to="/beats"
              data-testid="library-empty-cta"
              className="inline-block mt-5 h-11 px-5 rounded-full font-bold bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 text-white"
            >
              Browse Beats
            </Link>
          </div>
        ) : (
          <div className="space-y-3" data-testid="library-list">
            <p className="text-xs text-gray-400" data-testid="library-count">
              {purchases.length} {purchases.length === 1 ? "license" : "licenses"}
            </p>
            {purchases.map((p) => (
              <LicensedBeatCard key={p.sale_id || p.session_id} purchase={p} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LicensedBeatsPage;
